// Minimal OpenTimestamps proof walker. Implements only what we need for the
// server-side maturity flow:
//   1. Parse a serialized Timestamp tree rooted at a known msg.
//   2. Walk the tree, applying operations, capturing the running msg.
//   3. Find PendingAttestations + their commitment-at-attestation values.
//   4. Find BitcoinBlockHeaderAttestations + their block heights.
//   5. Merge an upgraded subtree into a parent tree at a matching node.
//   6. Re-serialize the merged tree.
//
// Wire format (per python-opentimestamps / OTS spec):
//   Timestamp ::= ATTESTATION* OP*-TAIL
//   ATTESTATION ::= 0x00 <8-byte-tag> <varint-len> <payload>
//   OP ::= <op-byte> <op-args> recurse
//   Multi-branch marker: 0xff 0x00 precedes each child op except the last.
//
// We support the operations we observe in real TSA/calendar pending proofs:
// SHA256, APPEND, PREPEND. RIPEMD160 / SHA1 / KECCAK / REVERSE / HEXLIFY are
// out of scope (not used by alice/bob/finney). If a calendar ever returns
// one we don't recognize, parse() throws — fail-loud is the right call for
// crypto code.

const OP_SHA256 = 0x08;
const OP_APPEND = 0xf0;
const OP_PREPEND = 0xf1;

const ATTEST_MARKER = 0x00;
const FORK_MARKER = 0xff;

const TAG_PENDING = new Uint8Array([
  0x83, 0xdf, 0xe3, 0x0d, 0x2e, 0xf9, 0x0c, 0x8e,
]);
const TAG_BITCOIN = new Uint8Array([
  0x05, 0x88, 0x96, 0x0d, 0x73, 0xd7, 0x19, 0x01,
]);

export type PendingAttestation = {
  type: "pending";
  uri: string;
};
export type BitcoinAttestation = {
  type: "bitcoin";
  height: number;
};
// Unknown attestation tags (Litecoin / Ethereum / future) — preserved
// verbatim for round-trip serialization. Walker doesn't try to interpret
// them and `collectPending` skips them so the maturity poll loop doesn't
// dial fake URIs.
export type UnknownAttestation = {
  type: "unknown";
  tag: Uint8Array;
  payload: Uint8Array;
};
export type Attestation =
  | PendingAttestation
  | BitcoinAttestation
  | UnknownAttestation;

export type TimestampNode = {
  msg: Uint8Array;
  attestations: Attestation[];
  children: { op: Op; child: TimestampNode }[];
};

export type Op =
  | { kind: "sha256" }
  | { kind: "append"; data: Uint8Array }
  | { kind: "prepend"; data: Uint8Array };

class Reader {
  i = 0;
  constructor(public buf: Uint8Array) {}
  remaining(): number {
    return this.buf.length - this.i;
  }
  peek(): number {
    if (this.i >= this.buf.length) throw new Error("ots-walker: EOF");
    return this.buf[this.i]!;
  }
  readByte(): number {
    if (this.i >= this.buf.length) throw new Error("ots-walker: EOF");
    return this.buf[this.i++]!;
  }
  readBytes(n: number): Uint8Array {
    if (this.i + n > this.buf.length) throw new Error("ots-walker: EOF");
    const out = this.buf.subarray(this.i, this.i + n);
    this.i += n;
    return out;
  }
  readVarUint(): number {
    let v = 0;
    let mult = 1;
    while (true) {
      const b = this.readByte();
      v += (b & 0x7f) * mult;
      if ((b & 0x80) === 0) return v;
      mult *= 128;
      if (mult > Number.MAX_SAFE_INTEGER)
        throw new Error("ots-walker: varint too long");
    }
  }
  readVarBytes(): Uint8Array {
    const n = this.readVarUint();
    return this.readBytes(n);
  }
}

class Writer {
  parts: Uint8Array[] = [];
  writeByte(b: number): void {
    this.parts.push(new Uint8Array([b]));
  }
  writeBytes(b: Uint8Array): void {
    this.parts.push(b);
  }
  writeVarUint(n: number): void {
    const out: number[] = [];
    let v = n;
    while (v >= 0x80) {
      out.push((v & 0x7f) | 0x80);
      v = Math.floor(v / 128);
    }
    out.push(v & 0x7f);
    this.parts.push(new Uint8Array(out));
  }
  writeVarBytes(b: Uint8Array): void {
    this.writeVarUint(b.length);
    this.writeBytes(b);
  }
  output(): Uint8Array {
    const total = this.parts.reduce((s, p) => s + p.length, 0);
    const out = new Uint8Array(total);
    let off = 0;
    for (const p of this.parts) {
      out.set(p, off);
      off += p.length;
    }
    return out;
  }
}

async function applyOp(op: Op, msg: Uint8Array): Promise<Uint8Array> {
  if (op.kind === "sha256") {
    const digest = await crypto.subtle.digest(
      "SHA-256",
      msg as BufferSource,
    );
    return new Uint8Array(digest);
  }
  if (op.kind === "append") {
    const out = new Uint8Array(msg.length + op.data.length);
    out.set(msg, 0);
    out.set(op.data, msg.length);
    return out;
  }
  // prepend
  const out = new Uint8Array(op.data.length + msg.length);
  out.set(op.data, 0);
  out.set(msg, op.data.length);
  return out;
}

function readOp(r: Reader): Op {
  const tag = r.readByte();
  if (tag === OP_SHA256) return { kind: "sha256" };
  if (tag === OP_APPEND) return { kind: "append", data: r.readVarBytes() };
  if (tag === OP_PREPEND) return { kind: "prepend", data: r.readVarBytes() };
  throw new Error(`ots-walker: unsupported op 0x${tag.toString(16)}`);
}

function writeOp(w: Writer, op: Op): void {
  if (op.kind === "sha256") {
    w.writeByte(OP_SHA256);
    return;
  }
  if (op.kind === "append") {
    w.writeByte(OP_APPEND);
    w.writeVarBytes(op.data);
    return;
  }
  w.writeByte(OP_PREPEND);
  w.writeVarBytes(op.data);
}

function readAttestation(r: Reader): Attestation {
  const tag = r.readBytes(8);
  const payload = r.readVarBytes();
  if (matchTag(tag, TAG_PENDING)) {
    // Payload is varbytes-wrapped UTF-8 URI
    const inner = new Reader(payload);
    const uriBytes = inner.readVarBytes();
    return { type: "pending", uri: new TextDecoder().decode(uriBytes) };
  }
  if (matchTag(tag, TAG_BITCOIN)) {
    const inner = new Reader(payload);
    const height = inner.readVarUint();
    return { type: "bitcoin", height };
  }
  // Unknown attestation: keep tag + payload bytes verbatim so we can
  // re-serialize without corruption.
  return { type: "unknown", tag: new Uint8Array(tag), payload };
}

function matchTag(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

function writeAttestation(w: Writer, att: Attestation): void {
  if (att.type === "pending") {
    w.writeBytes(TAG_PENDING);
    const inner = new Writer();
    inner.writeVarBytes(new TextEncoder().encode(att.uri));
    w.writeVarBytes(inner.output());
    return;
  }
  if (att.type === "bitcoin") {
    w.writeBytes(TAG_BITCOIN);
    const inner = new Writer();
    inner.writeVarUint(att.height);
    w.writeVarBytes(inner.output());
    return;
  }
  // unknown: write the original tag + payload verbatim
  w.writeBytes(att.tag);
  w.writeVarBytes(att.payload);
}

// Parse a Timestamp tree starting from `rootMsg`, consuming bytes from `r`.
async function parseTimestamp(
  r: Reader,
  rootMsg: Uint8Array,
): Promise<TimestampNode> {
  const node: TimestampNode = {
    msg: rootMsg,
    attestations: [],
    children: [],
  };

  // Loop: read attestations + ops until parent context ends. The OTS wire
  // format for a single-message Timestamp is a sequence of (attestations
  // and op-children) without explicit termination — the parent (or the
  // top-level deserializer) decides when to stop reading.
  //
  // For our use case (single-branch proofs from public calendars), the
  // structure is: one or more ops, then exactly one attestation at the
  // leaf. A Timestamp may also have multiple parallel children separated
  // by FORK markers (0xff 0x00). We support the multi-child form too.
  while (r.remaining() > 0) {
    const peek = r.peek();
    if (peek === ATTEST_MARKER) {
      r.readByte();
      node.attestations.push(readAttestation(r));
      continue;
    }
    if (peek === FORK_MARKER) {
      // 0xff alone marks a new sibling branch (per python-opentimestamps).
      // Read op + recurse, then loop for more siblings.
      r.readByte();
      const op = readOp(r);
      const childMsg = await applyOp(op, node.msg);
      const child = await parseTimestamp(r, childMsg);
      node.children.push({ op, child });
      continue;
    }
    // Final (bare) child for this node — no more siblings after this.
    const op = readOp(r);
    const childMsg = await applyOp(op, node.msg);
    const child = await parseTimestamp(r, childMsg);
    node.children.push({ op, child });
    return node;
  }
  return node;
}

export async function parseProof(
  bytes: Uint8Array,
  rootMsg: Uint8Array,
): Promise<TimestampNode> {
  const r = new Reader(bytes);
  const tree = await parseTimestamp(r, rootMsg);
  if (r.remaining() > 0) {
    throw new Error(
      `ots-walker: ${r.remaining()} trailing bytes after parse`,
    );
  }
  return tree;
}

// Recursively walk a tree, calling fn on every node (post-order).
export function walk(
  node: TimestampNode,
  fn: (n: TimestampNode) => void,
): void {
  for (const { child } of node.children) walk(child, fn);
  fn(node);
}

export type PendingAttestationSite = {
  uri: string;
  commitment: Uint8Array; // the running msg at this attestation
};

// Find every PendingAttestation in the tree along with the running msg at
// that point — what the calendar wants in its `GET /timestamp/{hex}` URL.
export function collectPending(node: TimestampNode): PendingAttestationSite[] {
  const out: PendingAttestationSite[] = [];
  walk(node, (n) => {
    for (const att of n.attestations) {
      if (att.type === "pending") {
        out.push({ uri: att.uri, commitment: n.msg });
      }
    }
  });
  return out;
}

// Find the first BitcoinBlockHeaderAttestation, returning its block height.
export function findBitcoin(node: TimestampNode): number | null {
  const site = findBitcoinSite(node);
  return site ? site.height : null;
}

// Find the first BitcoinBlockHeaderAttestation, returning its block height
// AND the running msg at that node — i.e. the value the BTC block's merkle
// tree commits to. The verifier compares this against the actual block's
// merkle root via a block-explorer lookup (slice 3b).
export function findBitcoinSite(
  node: TimestampNode,
): { height: number; terminalMsg: Uint8Array } | null {
  let result: { height: number; terminalMsg: Uint8Array } | null = null;
  walk(node, (n) => {
    if (result !== null) return;
    for (const att of n.attestations) {
      if (att.type === "bitcoin") {
        result = { height: att.height, terminalMsg: n.msg };
        return;
      }
    }
  });
  return result;
}

function attestationsEqual(a: Attestation, b: Attestation): boolean {
  if (a.type !== b.type) return false;
  if (a.type === "pending" && b.type === "pending") return a.uri === b.uri;
  if (a.type === "bitcoin" && b.type === "bitcoin") return a.height === b.height;
  if (a.type === "unknown" && b.type === "unknown") {
    return matchBytes(a.tag, b.tag) && matchBytes(a.payload, b.payload);
  }
  return false;
}

// Merge an upgraded subtree (rooted at the same msg as `target`) into the
// target. Dedupes attestations to avoid unbounded growth across cron
// cycles when a calendar's response repeats a still-pending attestation.
export function mergeAtMatchingMsg(
  target: TimestampNode,
  upgraded: TimestampNode,
): boolean {
  if (matchBytes(target.msg, upgraded.msg)) {
    for (const att of upgraded.attestations) {
      if (!target.attestations.some((t) => attestationsEqual(t, att))) {
        target.attestations.push(att);
      }
    }
    for (const c of upgraded.children) target.children.push(c);
    return true;
  }
  for (const { child } of target.children) {
    if (mergeAtMatchingMsg(child, upgraded)) return true;
  }
  return false;
}

function matchBytes(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

// Serialize a Timestamp tree back to wire format.
export function serializeProof(node: TimestampNode): Uint8Array {
  const w = new Writer();
  serializeNode(w, node);
  return w.output();
}

function serializeNode(w: Writer, node: TimestampNode): void {
  for (const att of node.attestations) {
    w.writeByte(ATTEST_MARKER);
    writeAttestation(w, att);
  }
  // Children: write FORK_MARKER (0xff alone) before all but the last child.
  for (let i = 0; i < node.children.length; i++) {
    const last = i === node.children.length - 1;
    if (!last) w.writeByte(FORK_MARKER);
    writeOp(w, node.children[i]!.op);
    serializeNode(w, node.children[i]!.child);
  }
}
